import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Trophy, Star, MapPin, Phone, TrendingUp, Award } from "lucide-react";

const MEDAL = ["🥇", "🥈", "🥉"];

function StarRow({ stars }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= stars ? "fill-amber-400 text-amber-500" : "text-slate-300"}`}/>
      ))}
    </div>
  );
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const MONTH_HI = ["जनवरी","फ़रवरी","मार्च","अप्रैल","मई","जून","जुलाई","अगस्त","सितम्बर","अक्टूबर","नवम्बर","दिसम्बर"];

export default function ReporterRatingsPage() {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/admin/reporter-ratings?month=${month}`).then((r) => setData(r.data)).catch(() => {});
  }, [month]);

  const [yr, mo] = month.split("-").map(Number);

  return (
    <div data-testid="reporter-ratings-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500"/>रिपोर्टर लीडरबोर्ड</h1>
          <p className="text-sm text-slate-500 mt-1">मासिक प्रदर्शन रैंकिंग · approved-count + acceptance-rate आधारित 5-star rating</p>
        </div>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="border border-slate-300 rounded-md px-3 py-2 text-sm font-mono"
          data-testid="ratings-month-picker"/>
      </div>

      {!data ? (
        <div className="p-10 text-center text-slate-500">लोड हो रहा है…</div>
      ) : (
        <>
          <div className="mb-4 flex items-center gap-3 text-sm text-slate-600">
            <span>महीना: <b className="text-[#0F172A]">{MONTH_HI[mo - 1]} {yr}</b></span>
            <span>·</span>
            <span>{data.totals.active} रिपोर्टर सक्रिय</span>
            <span>·</span>
            <span className="text-emerald-700">{data.totals.approved} प्रकाशित</span>
            <span>·</span>
            <span className="text-amber-700">{data.totals.pending} समीक्षाधीन</span>
          </div>

          {data.reporters.length === 0 ? (
            <div className="p-16 text-center text-slate-500 italic bg-white border border-dashed border-slate-200 rounded-lg">
              इस महीने अभी तक कोई रिपोर्टर सबमिशन नहीं
            </div>
          ) : (
            <>
              {/* Podium — top 3 */}
              {data.reporters.length >= 1 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {data.reporters.slice(0, 3).map((r, i) => (
                    <div key={r.staff_id} className={`rounded-xl p-5 border-2 shadow-md relative overflow-hidden ${
                      i === 0 ? "bg-gradient-to-br from-amber-50 to-yellow-100 border-amber-400" :
                      i === 1 ? "bg-gradient-to-br from-slate-50 to-slate-100 border-slate-400" :
                      "bg-gradient-to-br from-orange-50 to-orange-100 border-orange-400"
                    }`} data-testid={`podium-${r.staff_id}`}>
                      <div className="absolute top-2 right-3 text-5xl opacity-90">{MEDAL[i]}</div>
                      <div className="text-[10px] uppercase tracking-widest font-black" style={{ color: i === 0 ? "#B45309" : i === 1 ? "#475569" : "#C2410C" }}>
                        {i === 0 ? "TOP PERFORMER" : i === 1 ? "2ND PLACE" : "3RD PLACE"}
                      </div>
                      <div className="mt-2 flex items-center gap-3">
                        {r.photo_url ? (
                          <img src={r.photo_url} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-white shadow"/>
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-white shadow flex items-center justify-center text-2xl font-black text-slate-700">{r.name[0]}</div>
                        )}
                        <div className="min-w-0">
                          <div className="font-devnagri-heading font-black text-[#0F172A] text-lg leading-tight truncate">{r.name}</div>
                          <div className="text-[11px] text-slate-600">{r.designation} · <span className="font-mono">{r.staff_id}</span></div>
                          <div className="mt-1"><StarRow stars={r.stars}/></div>
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-1 text-center">
                        <div><div className="text-lg font-black text-emerald-700">{r.approved}</div><div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">प्रकाशित</div></div>
                        <div><div className="text-lg font-black text-amber-700">{r.acceptance}%</div><div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Accept</div></div>
                        <div><div className="text-lg font-black text-blue-700">{r.districts_covered}</div><div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">ज़िले</div></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Full leaderboard table */}
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 font-bold text-[#0F172A] flex items-center gap-2">
                  <Award className="w-4 h-4 text-amber-600"/>पूरी सूची
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500 font-bold">
                      <tr>
                        <th className="text-left px-4 py-2">रैंक</th>
                        <th className="text-left px-4 py-2">रिपोर्टर</th>
                        <th className="text-center px-4 py-2">रेटिंग</th>
                        <th className="text-center px-4 py-2">प्रकाशित</th>
                        <th className="text-center px-4 py-2">समीक्षाधीन</th>
                        <th className="text-center px-4 py-2">Accept %</th>
                        <th className="text-center px-4 py-2">ज़िले</th>
                        <th className="text-right px-4 py-2">संपर्क</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.reporters.map((r) => (
                        <tr key={r.staff_id} className="hover:bg-slate-50" data-testid={`row-${r.staff_id}`}>
                          <td className="px-4 py-3 font-black text-[#0F172A]">{r.rank <= 3 ? <span className="text-xl">{MEDAL[r.rank - 1]}</span> : `#${r.rank}`}</td>
                          <td className="px-4 py-3">
                            <div className="font-bold text-[#0F172A]">{r.name}</div>
                            <div className="text-[10px] text-slate-500 font-mono">{r.staff_id} · {r.designation}</div>
                          </td>
                          <td className="px-4 py-3 text-center"><StarRow stars={r.stars}/></td>
                          <td className="px-4 py-3 text-center"><span className="font-black text-emerald-700">{r.approved}</span></td>
                          <td className="px-4 py-3 text-center text-amber-700 font-bold">{r.pending}</td>
                          <td className="px-4 py-3 text-center"><span className={`font-bold ${r.acceptance >= 70 ? "text-emerald-700" : r.acceptance >= 40 ? "text-amber-700" : "text-rose-600"}`}>{r.acceptance}%</span></td>
                          <td className="px-4 py-3 text-center text-blue-700 font-bold">{r.districts_covered}</td>
                          <td className="px-4 py-3 text-right">
                            {r.phone && <a href={`tel:${r.phone}`} className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"><Phone className="w-3 h-3"/>{r.phone}</a>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="mt-3 text-[11px] text-slate-500 text-right">
                <b>रेटिंग सूत्र:</b> 60% approved-count vs top performer + 40% acceptance rate — 5 stars टॉप के लिए
              </div>
            </>
          )}
        </>
      )}

      <Link to="/admin/reporter-map" className="mt-6 inline-block text-sm text-slate-600 hover:text-[#0F172A] underline">← कवरेज मानचित्र</Link>
    </div>
  );
}
